import {CANONICAL_VERSION} from '../../content/canonical';
// Recipe detail + editor (Swift RecipeView).
// View mode shows amounts for "cook for N" (display only); edit mode works on a local draft
// that is saved on Done or when leaving the screen.
// Switching modes never moves the page: texts and values turn into fields in place, controls
// that only one mode has fade in and out on spots kept for them (or open in a sheet).
import React, { useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useNav, type DetailParams } from '../navigation';
import { currencyCode, useDiet, useHeader, useToday } from '../hooks';
import { newId, useAllIngredients, useAllUtensils, useLibrary, useRecipe } from '../store/library';
import { useSettings } from '../store/settings';
import { useShopping } from '../store/shopping';
import { usePlanner } from '../store/planner';
import { useProgress } from '../store/progress';
import { useTimers } from '../store/timers';
import { scaleIngredient, scaleUtensil } from '../logic/scaling';
import { formatQuantity, ingredientLine } from '../logic/format';
import { resolveUnit, toSystem } from '../logic/units';
import { dietBadge, pantryBadge, type Badge } from '../logic/badges';
import { recipeIngredientIds, type DietStatus } from '../logic/diets';
import DietMark from '../components/DietMark';
import { detectTimers, formatClock } from '../logic/timerText';
import { buildMatcher, linkSteps, withDetectedIngredients } from '../logic/ingredientMatch';
import { exportLibrary } from '../store/backup';
import { exportText } from '../platform/files';
import { copyText } from '../platform/clipboard';
import { applyPickedMedia } from '../logic/mediaDraft';
import { pickPhoto, savePhoto } from '../platform/media';
import { locale } from '../i18n';
import Icon, { type IconName } from '../components/Icon';
import Photo from '../components/Photo';
import ItemSearchBox from '../components/ItemSearchBox';
import LineEditorSheet, { type EditableLine } from '../components/LineEditorSheet';
import NutritionSheet from '../components/NutritionSheet';
import Sheet from '../components/Sheet';
import { EditableText, Fade, Swap } from '../components/Motion';
import { CookbookPickerSheet, PlanPickerSheet } from '../components/Pickers';
import { confirm, notify, showMenu } from '../components/Dialogs';
import { Body, Button, Caption, Chip, Field, IconButton, Row, Screen, Section, SectionHeader, Stepper } from '../components/ui';
import { addRecipesToCookbook } from './AllRecipesScreen';
import { DIETS, INGREDIENT_AISLES, UTENSIL_AISLES, type IngredientLine, type Recipe, type Step, type UtensilLine } from '../types';
import { font, radius, spacing, usePalette } from '../theme';
import { useLabels } from '../labels';

type Props = NativeStackScreenProps<DetailParams, 'Recipe'>;

export default function RecipeScreen({ route, navigation }: Props) {
  const { t } = useTranslation();
  const labels = useLabels();
  const nav = useNav();
  const p = usePalette();
  const today = useToday();
  const { width } = useWindowDimensions();
  const { recipe: source, readOnly } = useRecipe(route.params.id);

  const put = useLibrary(s => s.put);
  const ingredients = useAllIngredients();
  const utensils = useAllUtensils();
  const units = useLibrary(s => s.units);
  const settings = useSettings();
  const dietCheck = useDiet();
  const confirmDiet = useLibrary(s => s.confirmDiet);
  const pantry = useShopping(s => s.pantry);
  const addToGrocery = useShopping(s => s.addRecipes);
  const plan = usePlanner(s => s.plan);
  // Saving a cookbook recipe to the library swaps the id in place; its log entries still count.
  const shownIds = useRef(new Set<string>()).current;
  shownIds.add(route.params.id);
  const cooked = useProgress(s => s.log.some(e => !!e.recipeId && shownIds.has(e.recipeId)));
  const startTimer = useTimers(s => s.start);

  const [editing, setEditing] = useState(!!route.params.edit && !readOnly);
  const [draft, setDraft] = useState<Recipe | undefined>(source);
  const [cookFor, setCookFor] = useState(source?.servings ?? 1);
  const [lineTarget, setLineTarget] = useState<{ edit: EditableLine; index: number; stepIndex?: number } | null>(null);
  const [sheet, setSheet] = useState<'nutrition' | 'planner' | 'cookbook' | 'diets' | 'addItem' | null>(null);
  const [stepSheet, setStepSheet] = useState<string | null>(null);
  // Only cookbook recipes get the "save to your library" note; it fades out once saved.
  const [cameReadOnly] = useState(readOnly);
  const [newStep, setNewStep] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!editing) setDraft(source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.updatedAt, source?.id, editing]);
  // "Cook for" only resets when the servings change (or editing ends), not on every save.
  useEffect(() => {
    if (!editing) setCookFor(source?.servings ?? 1);
  }, [source?.servings, editing]);

  const r = editing ? draft : source;

  const save = (next: Recipe | undefined = draft) => {
    if (!next || readOnly) return;
    put('recipes', next);
  };

  // Save the draft if the user leaves while editing.
  const latest = useRef({ editing, draft });
  latest.current = { editing, draft };
  useEffect(() => navigation.addListener('beforeRemove', () => latest.current.editing && save(latest.current.draft)), [navigation]); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (patch: Partial<Recipe>) => {
    const current = latest.current.draft;
    if (!current) return;
    const next = { ...current, ...patch };
    latest.current.draft = next;
    setDraft(next);
  };

  const saveToLibrary = () => {
    if (!source) return;
    const id=source.id;
    put('recipes',{...source,canonical:{id,version:CANONICAL_VERSION}});
    // Same screen, new id: the page stays put and only the note fades out (no navigation animation).
    navigation.setParams({ id });
    notify(t('path.savedToLibrary'));
  };

  const menu = () =>
    showMenu([
        { label: t('recipe.nutrition'), icon: 'nutrition' as IconName, onPress: () => setSheet('nutrition') },
        { label: t('library.addToPlanner'), icon: 'calendarAdd' as IconName, onPress: () => setSheet('planner') },
        ...(!readOnly ? [{ label: t('library.addToCookbook'), icon: 'cookbook' as IconName, onPress: () => setSheet('cookbook') }] : []),
        {
          label: t('recipe.addToGrocery'), icon: 'cartAdd' as IconName,
          onPress: () => source && notify(addToGrocery([source]) ? t('recipe.addedToGrocery') : t('recipe.alreadyOnGrocery')),
        },
        { label: t('path.logMeal'), icon: 'camera' as IconName, onPress: () => nav.navigate('LogMeal', { recipeId: route.params.id }) },
      ...(readOnly ? [{ label: t('path.saveToLibrary'), icon: 'import' as IconName, onPress: saveToLibrary }] : []),
      { label: t('common.copyTitle'), icon: 'copy', onPress: () => copyText(source?.title ?? '') },
      ...(!readOnly ? [{ label: t('common.share'), icon: 'share' as IconName, onPress: () => exportText(`${source?.title ?? 'recipe'}.json`, exportLibrary({ recipes: [route.params.id] })) }] : []),
    ]);

  useHeader(
    {
      title: source?.title ?? '',
      headerRight: () =>
        editing ? (
          <Row gap={spacing.m}>
            {selected.size ? (
              <IconButton
                icon="trash"
                color={p.danger}
                label={t('common.deleteSelection')}
                onPress={async () => {
                  if (!(await confirm(t('common.deleteConfirmTitle'), t('common.deleteConfirmBody'), t('common.deleteSelection')))) return;
                  deleteSelected();
                }}
              />
            ) : null}
            <IconButton icon="check" label={t('common.done')} onPress={() => { save(latest.current.draft); setEditing(false); setSelected(new Set()); }} />
          </Row>
        ) : (
          <Row gap={spacing.m}>
            <IconButton icon="play" color={p.tint} label={t('recipe.cookMode')} onPress={() => nav.navigate('CookMode', { recipeId: route.params.id, cookFor })} />
            <IconButton icon="more" label={t('common.more')} onPress={menu} />
            {!readOnly ? <IconButton icon="edit" label={t('common.edit')} onPress={() => {
              if(source?.canonical){const {canonical,...copy}=source;const next={...copy,id:newId(),derivedFrom:{recipeId:canonical.id,version:canonical.version},steps:source.steps.map(step=>({...step,id:newId()}))};put('recipes',next);navigation.setParams({id:next.id});setDraft(next);}
              else setDraft(source);setEditing(true);
            }} /> : null}
          </Row>
        ),
    },
    [editing, source, selected, readOnly, cookFor, p, t],
  );

  if (!r) return <Screen><Body>{t('common.empty')}</Body></Screen>;

  // ---------- derived display ----------
  const lineText = (l: IngredientLine) => {
    const title = ingredients[l.ingredientId]?.title ?? l.ingredientId;
    let value = editing ? scaleIngredient(l.amount, 1, 1) : scaleIngredient(l.amount, r.servings, cookFor);
    let unit = resolveUnit(l.unit, units);
    if (!editing && settings.convertUnits && unit) ({ value, unit } = toSystem(value, unit, settings.unitSystem));
    return ingredientLine(formatQuantity(value, unit), unit?.symbol ?? null, title, t('common.of'));
  };
  const utensilText = (l: UtensilLine) =>
    ingredientLine(String(editing ? l.amount : scaleUtensil(l.amount, l.scaleEvery, cookFor)), null, utensils[l.utensilId]?.title ?? l.utensilId, t('common.of'));

  const allIngredientIds = recipeIngredientIds(r);
  const allUtensilIds = [...new Set([...r.utensils.map(l => l.utensilId), ...r.steps.flatMap(s => s.utensils.map(l => l.utensilId))])];
  const review = dietCheck.review(r);
  const diet = dietBadge(review, editing && newStep.length > 0);
  const pantryState = pantryBadge(allIngredientIds, allUtensilIds, pantry, (k, id) => (k === 'ingredient' ? ingredients[id]?.title : utensils[id]?.title) ?? id, editing && newStep.length > 0);

  // Aisle grouping with the Swift "sub-headers only if some aisle has ≥3 items" rule.
  type Entry = { key: string; aisle: string; title: string; text: string; icon: IconName; open: () => void };
  const entries: Entry[] = [
    ...r.ingredients.map((l, i) => ({
      key: `i:${i}`, aisle: ingredients[l.ingredientId]?.aisle ?? 'other', title: ingredients[l.ingredientId]?.title ?? '', text: lineText(l), icon: 'ingredient' as IconName,
      open: () => setLineTarget({ edit: { kind: 'ingredient', line: l }, index: i }),
    })),
    ...r.utensils.map((l, i) => ({
      key: `u:${i}`, aisle: `u.${utensils[l.utensilId]?.aisle ?? 'other'}`, title: utensils[l.utensilId]?.title ?? '', text: utensilText(l), icon: 'utensil' as IconName,
      open: () => setLineTarget({ edit: { kind: 'utensil', line: l }, index: i }),
    })),
  ];
  const aisleOrder = [...INGREDIENT_AISLES, ...UTENSIL_AISLES.map(a => `u.${a}`)];
  const counts = new Map<string, number>();
  entries.forEach(e => counts.set(e.aisle, (counts.get(e.aisle) ?? 0) + 1));
  const withHeaders = [...counts.values()].some(n => n >= 3);
  const groups = aisleOrder
    .map(aisle => ({ aisle, items: entries.filter(e => e.aisle === aisle) }))
    .filter(g => g.items.length)
    .map(g => ({ ...g, items: withHeaders ? [...g.items].sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })) : g.items }));

  // ---------- edit helpers ----------
  const deleteSelected = () => {
    setDraft(d => {
      if (!d) return d;
      const ingIdx = new Set([...selected].filter(k => k.startsWith('i:')).map(k => Number(k.slice(2))));
      const utIdx = new Set([...selected].filter(k => k.startsWith('u:')).map(k => Number(k.slice(2))));
      const stepIds = new Set([...selected].filter(k => k.startsWith('s:')).map(k => k.slice(2)));
      const removedIng = new Set(d.ingredients.filter((_, i) => ingIdx.has(i)).map(l => l.ingredientId));
      const removedUt = new Set(d.utensils.filter((_, i) => utIdx.has(i)).map(l => l.utensilId));
      return {
        ...d,
        ingredients: d.ingredients.filter((_, i) => !ingIdx.has(i)),
        utensils: d.utensils.filter((_, i) => !utIdx.has(i)),
        // Swift: removing a recipe-level item also removes its step links.
        steps: d.steps
          .filter(s => !stepIds.has(s.id))
          .map(s => ({ ...s, ingredients: s.ingredients.filter(l => !removedIng.has(l.ingredientId)), utensils: s.utensils.filter(l => !removedUt.has(l.utensilId)) })),
      };
    });
    setSelected(new Set());
  };
  const toggleSel = (key: string) => setSelected(s => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key);
    else n.add(key);
    return n;
  });
  // On-device helpers (logic/ingredientMatch.ts): write only the instructions, then let Chefi list and link the ingredients.
  const findIngredients = () => {
    if (!draft) return;
    const { recipe: next, added } = withDetectedIngredients(draft, buildMatcher(Object.values(ingredients)));
    setDraft(linkSteps(next, ingredients));
    notify(added.length ? t('recipe.foundIngredients', { count: added.length }) : t('recipe.foundNoIngredients'));
  };
  const linkIngredients = () => {
    if (!draft) return;
    const next = linkSteps(draft, ingredients);
    setDraft(next);
    notify(t('recipe.linkedIngredients', { count: next.steps.filter(s => s.ingredients.length).length }));
  };
  const updateStep = (index: number, patch: Partial<Step>) => {
    const stepId = r.steps[index]?.id;
    const current = latest.current.draft;
    if (!current || !stepId || !useLibrary.getState().recipes[current.id]) return;
    const next = { ...current, steps: current.steps.map(s => s.id === stepId ? { ...s, ...patch } : s) };
    update({ steps: next.steps });
    if ('image' in patch) put('recipes', next);
  };
  const moveStep = (index: number, dir: -1 | 1) => {
    const steps = [...r.steps];
    const j = index + dir;
    if (j < 0 || j >= steps.length) return;
    [steps[index], steps[j]] = [steps[j], steps[index]];
    update({ steps });
  };
  const commitNewStep = () => {
    const title = newStep.replace(/\n/g, ' ').trim();
    if (!title) return;
    update({ steps: [...r.steps, { id: newId(), title, explainer: '', ingredients: [], utensils: [], timers: [] }] });
    setNewStep('');
  };

  const wide = width >= 700;
  const badgeIcon = (b: Badge): { icon: IconName; color: string } =>
    b.state === 'satisfied' ? { icon: 'checkCircle', color: p.success } : b.state === 'failed' ? { icon: 'close', color: p.danger } : b.state === 'warning' ? { icon: 'warning', color: p.warning } : { icon: 'help', color: p.secondaryText };
  const dietText = () =>
    t(b2key(diet), { diets: diet.items.map(d => t(`diets.${d}` as 'diets.vegan')).join(', ') }) + (editing && newStep.length > 0 ? t('recipe.pendingEdits') : '');
  const reasonText = (s: DietStatus) =>
    s.reasons
      .map(x => (x.kind === 'dish' ? t('diet.reasonDish') : x.kind === 'undeclared' ? t('diet.reasonUndeclared', { item: ingredients[x.id]?.title ?? x.id }) : ingredients[x.id]?.title ?? x.id))
      .join(', ');
  const statusText = (s: DietStatus) =>
    s.state === 'breaks'
      ? t('diet.statusBreaks', { reasons: reasonText({ ...s, reasons: s.reasons.filter(x => x.kind === 'ingredient') }) })
      : s.state === 'issue'
      ? t('diet.statusIssue', { reasons: reasonText(s) })
      : s.state === 'confirmed'
        ? t('diet.statusConfirmed') + (s.reasons.length ? ` ${t('diet.statusConfirmedFlags', { reasons: reasonText(s) })}` : '')
        : t('diet.statusUnreviewed');
  const pantryText = () => t(b2keyPantry(pantryState), { items: pantryState.items.join(', ') }) + (pantryState.state === 'warning' ? t('recipe.pendingEdits') : '');

  const currency = currencyCode();

  const hero = (
    <View style={{ width: wide ? '40%' : '100%' }}>
      <Photo media={r.image} seed={[...shownIds][0]} icon="recipe" style={{ width: '100%', aspectRatio: 4 / 3 }} iconSize={56} />
      {/* Overlays on the photo: they fade without pushing anything around. */}
      <Fade visible={editing} style={{ position: 'absolute', right: spacing.s, bottom: spacing.s }}>
        <Button
          label={t('recipe.heroMedia')}
          icon="camera"
          variant="secondary"
          style={{ minHeight: 40 }}
          onPress={async () => {
            const id = r.id;
            await applyPickedMedia(pickPhoto, savePhoto,
              () => useLibrary.getState().recipes[id] ? latest.current.draft : undefined,
              (current, image) => { update({ image }); put('recipes', { ...current, image }); });
          }} iconOnly />
      </Fade>
      {cameReadOnly ? (
        <Fade visible={readOnly} style={labels ? { position: 'absolute', left: spacing.s, right: spacing.s, bottom: spacing.s, backgroundColor: p.card, borderRadius: radius.l, padding: spacing.m, gap: spacing.s } : { position: 'absolute', right: spacing.s, bottom: spacing.s }}>
          {labels ? <Caption>{t('recipe.readOnlyNote')}</Caption> : null}
          <Button label={t('path.saveToLibrary')} icon="import" variant="primary" onPress={saveToLibrary} style={{ minHeight: 40 }} iconOnly />
        </Fade>
      ) : null}
    </View>
  );

  const titleStyle = { fontSize: font.title, fontWeight: '800' as const, color: p.text, lineHeight: 34 };
  const bodyStyle = { fontSize: font.body, color: p.text, lineHeight: 22 };
  const minutes = splitAround(t('common.minutes', { count: r.durationMin }), String(r.durationMin));
  const price = priceParts(r.priceCents / 100, currency);
  const lastGroup = groups[groups.length - 1]?.aisle;
  const addItemChip = (
    <Fade visible={editing}>
      <Chip label={t('recipe.addItems')} icon="plus" onPress={() => setSheet('addItem')} iconOnly />
    </Fade>
  );

  const content = (
    <View style={{ flex: 1, gap: spacing.l }}>
      <View style={{ gap: spacing.s }}>
        <EditableText editing={editing} value={r.title} onChangeText={v => update({ title: v.replace(/\n/g, ' ') })} style={titleStyle} accessibilityLabel={t('editors.title')} />
        <DietMark flags={dietCheck.issues(r)} size={16} />
      </View>

      {/* Same chips in both modes: values become editable in place, view-only chips dim. */}
      <Row gap={spacing.s}>
        <ValueChip icon="clock" editing={editing} text={String(r.durationMin)} prefix={minutes.before} suffix={minutes.after} keyboardType="number-pad" label={t('recipe.duration')}
          onChange={v => update({ durationMin: Math.max(0, parseInt(v, 10) || 0) })} />
        <Row gap={4}>
          <Icon name="servings" size={18} color={p.secondaryText} />
          {editing ? (
            <Stepper label={t('recipe.servings')} value={r.servings} onChange={v => update({ servings: v })} />
          ) : (
            <Stepper label={t('recipe.cookFor')} value={cookFor} onChange={setCookFor} />
          )}
        </Row>
        <Chip icon={labels ? badgeIcon(diet).icon : 'diet'} color={badgeIcon(diet).color} label={t('recipe.diet')} onPress={editing ? () => setSheet('diets') : () => notify(dietText(), t('recipe.diet'))} iconOnly />
        <Fade visible={!editing} dim={0.35}>
          <Chip icon={labels ? badgeIcon(pantryState).icon : 'pantry'} color={badgeIcon(pantryState).color} label={t('recipe.pantry')} onPress={() => notify(pantryText(), t('recipe.pantry'))} iconOnly />
        </Fade>
        {cooked ? (
          <Fade visible={!editing} dim={0.35}>
            <Chip icon="checkCircle" color={p.success} label={t('path.cookedBadge')} iconOnly />
          </Fade>
        ) : null}
        <Fade visible={!editing} dim={0.35}>
          <Chip icon="unit" label={t('recipe.convertUnits')} selected={settings.convertUnits} onPress={() => settings.set({ convertUnits: !settings.convertUnits })} iconOnly />
        </Fade>
        {/* Last, so an unset price leaves no hole in view mode. */}
        <Fade visible={editing || r.priceCents > 0}>
          <ValueChip icon="price" editing={editing} text={price.number} prefix={price.before} suffix={price.after} keyboardType="decimal-pad" label={`${t('recipe.price')} (${currency})`}
            onChange={v => update({ priceCents: Math.max(0, Math.round(parseDecimal(v) * 100)) })} />
        </Fade>
      </Row>

      {review.length ? (
        <Section title={t('diet.yourDiets')} icon="diet" right={<IconButton icon="help" label={t('dietRules.title')} onPress={() => nav.navigate('Info', { page: 'dietRules' })} />} iconOnly>
          {review.map(s => (
            <View key={s.diet} style={{ backgroundColor: p.card, borderRadius: radius.l, padding: spacing.m, gap: spacing.s }}>
              <Row gap={spacing.s} style={{ flexWrap: 'nowrap' }}>
                <Icon
                  name={s.state === 'confirmed' ? 'checkCircle' : s.state === 'breaks' ? 'close' : s.state === 'issue' ? 'warning' : 'help'}
                  size={20}
                  color={s.state === 'confirmed' ? p.success : s.state === 'breaks' ? p.danger : s.state === 'issue' ? p.warning : p.secondaryText}
                />
                <Text style={{ flex: 1, fontSize: font.body, fontWeight: '600', color: p.text }}>{t(`diets.${s.diet}`)}</Text>
                {s.state === 'breaks' ? null : (
                  <Fade visible={!editing} dim={0.35}>
                    <Button
                      label={s.state === 'confirmed' ? t('diet.withdraw') : t('diet.confirmFits')}
                      variant={s.state === 'confirmed' ? 'plain' : 'secondary'}
                      onPress={() => confirmDiet(r.id, s.diet, s.state !== 'confirmed')}
                      style={{ minHeight: 36, paddingHorizontal: spacing.m }}
                    />
                  </Fade>
                )}
              </Row>
              <Caption>{statusText(s)}</Caption>
            </View>
          ))}
        </Section>
      ) : null}

      <Section title={t('recipe.aboutDish')} icon="info" iconOnly>
        <EditableText editing={editing} value={r.explainer} onChangeText={v => update({ explainer: v })} style={bodyStyle} placeholder={t('common.notes')} />
      </Section>

      <Section title={t('recipe.youWillNeed')} icon="checklist" iconOnly>
        {groups.map(g => (
          <View key={g.aisle} style={{ gap: spacing.s }}>
            {withHeaders ? (
              <SectionHeader title={g.aisle === 'other' ? t('recipe.needsAisle') : t(`aisles.${g.aisle.replace(/^u\./, '')}` as 'aisles.other')} color={g.aisle === 'other' ? p.warning : undefined} />
            ) : null}
            <Row>
              {g.items.map(e => (
                <ItemChip
                  key={e.key}
                  label={e.text}
                  icon={e.icon}
                  editing={editing}
                  selected={selected.has(e.key)}
                  onToggle={() => toggleSel(e.key)}
                  onOpen={readOnly ? undefined : e.open}
                />
              ))}
              {g.aisle === lastGroup ? addItemChip : null}
            </Row>
          </View>
        ))}
        {groups.length ? null : <Row>{addItemChip}</Row>}
      </Section>

      <Section title={t('recipe.instructions')} icon="recipe" style={{ gap: spacing.xl }} iconOnly>
        {r.steps.map((step, i) => (
          <StepView
            key={step.id}
            step={step}
            index={i}
            editing={editing}
            selected={selected.has(`s:${step.id}`)}
            onToggle={() => toggleSel(`s:${step.id}`)}
            itemTitle={(kind, id) => (kind === 'ingredient' ? ingredients[id]?.title : utensils[id]?.title) ?? id}
            onChange={patch => updateStep(i, patch)}
            onOpen={() => setStepSheet(step.id)}
            onStartTimer={(label, seconds) => {
              startTimer(label || r.title, r.id, step.id, seconds);
              notify(`${label} · ${formatClock(seconds)}`, t('timers.title'));
            }}
          />
        ))}
        <Fade visible={editing}>
          {editing ? (
            <TextInput
              value={newStep}
              onChangeText={v => (v.includes('\n') ? (setNewStep(v), setTimeout(commitNewStep, 0)) : setNewStep(v))}
              onSubmitEditing={commitNewStep}
              onBlur={commitNewStep}
              placeholder={t('recipe.addInstruction')}
              placeholderTextColor={p.secondaryText}
              returnKeyType="done"
              blurOnSubmit={false}
              style={{ backgroundColor: p.card, color: p.text, borderRadius: radius.m, paddingHorizontal: spacing.m, fontSize: font.body, height: 48 }}
            />
          ) : (
            <View style={{ height: 48 }} />
          )}
        </Fade>
        {editing && r.steps.length ? (
          <Row>
            <Button label={t('recipe.findIngredients')} icon="magic" variant="secondary" onPress={findIngredients} />
            {r.ingredients.length ? <Button label={t('recipe.linkIngredients')} icon="link" variant="secondary" onPress={linkIngredients} /> : null}
          </Row>
        ) : null}
      </Section>
    </View>
  );

  const sheetStep = r.steps.find(s => s.id === stepSheet);
  const sheetIndex = sheetStep ? r.steps.indexOf(sheetStep) : -1;

  return (
    <>
      <Screen>
        {wide ? (
          <View style={{ flexDirection: 'row', gap: spacing.xl, alignItems: 'flex-start' }}>
            {hero}
            {content}
          </View>
        ) : (
          <>
            {hero}
            {content}
          </>
        )}
      </Screen>

      <LineEditorSheet
        target={lineTarget?.edit ?? null}
        onClose={() => setLineTarget(null)}
        onSave={edited => {
          if (!lineTarget) return;
          const base = editing ? r : source!;
          const next: Recipe =
            edited.kind === 'ingredient'
              ? { ...base, ingredients: base.ingredients.map((l, i) => (i === lineTarget.index ? edited.line : l)) }
              : { ...base, utensils: base.utensils.map((l, i) => (i === lineTarget.index ? edited.line : l)) };
          if (editing) setDraft(next);
          else save(next); // Swift allowed editing amounts from view mode
          setLineTarget(null);
        }}
      />
      <StepSheet
        step={sheetStep}
        index={sheetIndex}
        count={r.steps.length}
        recipe={r}
        itemTitle={id => ingredients[id]?.title ?? id}
        onClose={() => setStepSheet(null)}
        onChange={patch => updateStep(sheetIndex, patch)}
        onMove={dir => moveStep(sheetIndex, dir)}
        onDelete={() => {
          setStepSheet(null);
          update({ steps: r.steps.filter(s => s.id !== stepSheet) });
        }}
      />
      <Sheet visible={sheet === 'addItem'} onClose={() => setSheet(null)} title={t('recipe.addItems')}>
        <ItemSearchBox
          excludeIngredients={new Set(r.ingredients.map(l => l.ingredientId))}
          excludeUtensils={new Set(r.utensils.map(l => l.utensilId))}
          onPick={pick =>
            pick.kind === 'ingredient'
              ? update({ ingredients: [...r.ingredients, { ingredientId: pick.id, amount: pick.quantity.amount, unit: pick.quantity.unit }] })
              : update({ utensils: [...r.utensils, { utensilId: pick.id, amount: Math.max(1, pick.quantity.amount.value), scaleEvery: null }] })
          }
        />
      </Sheet>
      <>
        <Sheet visible={sheet === 'diets'} onClose={() => setSheet(null)} title={t('diet.recipeBreaks')}>
          <Body secondary>{dietText()}</Body>
          <Caption>{t('diet.recipeBreaksNote')}</Caption>
          <Row>
            {DIETS.map(d => {
              const on = !!r.notDiet?.includes(d);
              return <Chip key={d} label={t(`diets.${d}`)} icon={on ? 'close' : undefined} selected={on} onPress={() => update({ notDiet: on ? r.notDiet?.filter(x => x !== d) : [...(r.notDiet ?? []), d] })} />;
            })}
          </Row>
        </Sheet>
        <NutritionSheet visible={sheet === 'nutrition'} onClose={() => setSheet(null)} lines={r.ingredients} servings={r.servings} title={t('recipe.nutrition')} />
        <PlanPickerSheet
          visible={sheet === 'planner'}
          today={today}
          onClose={() => setSheet(null)}
          onPick={(day, slot) => {
            plan(day, slot, [r.id]);
            setSheet(null);
            notify(t('planner.scheduled', { date: day }));
          }}
        />
        <CookbookPickerSheet
          visible={sheet === 'cookbook'}
          onClose={() => setSheet(null)}
          onPick={id => {
            addRecipesToCookbook(id, [r.id], t('library.defaultSection'));
            setSheet(null);
            notify(t('library.addedToCookbook', { title: useLibrary.getState().cookbooks[id]?.title ?? '' }));
          }}
        />
      </>
    </>
  );
}

/** "25 min" around "25" → { before: '', after: ' min' }, so the number can be edited in place. */
function splitAround(text: string, part: string) {
  const i = text.indexOf(part);
  return i < 0 ? { before: '', after: ` ${text}` } : { before: text.slice(0, i), after: text.slice(i + part.length) };
}

/** Currency text split into symbol and number, e.g. "CHF 12.50" → "CHF ", "12.50", "". */
function priceParts(value: number, currency: string) {
  const parts = new Intl.NumberFormat(locale(), { style: 'currency', currency }).formatToParts(value);
  const isNumber = (type: string) => ['integer', 'group', 'decimal', 'fraction'].includes(type);
  const first = parts.findIndex(x => isNumber(x.type));
  const last = parts.length - 1 - [...parts].reverse().findIndex(x => isNumber(x.type));
  const join = (xs: typeof parts) => xs.map(x => x.value).join('');
  return { before: join(parts.slice(0, first)), number: join(parts.slice(first, last + 1)), after: join(parts.slice(last + 1)) };
}

/** "1'234,5" / "1,234.50" / "12." → number; the last separator followed by 1–2 digits is the decimal one. */
export function parseDecimal(text: string): number {
  const s = text.replace(/[^\d.,]/g, '');
  const i = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
  if (i < 0 || s.length - i - 1 > 2) return Number(s.replace(/[.,]/g, '')) || 0;
  return Number(`${s.slice(0, i).replace(/[.,]/g, '')}.${s.slice(i + 1)}`) || 0;
}

/** A chip whose value turns into a field in place (duration, price). */
function ValueChip({
  icon, editing, text, prefix, suffix, onChange, keyboardType, label,
}: { icon: IconName; editing: boolean; text: string; prefix: string; suffix: string; onChange: (text: string) => void; keyboardType: 'number-pad' | 'decimal-pad'; label: string }) {
  const p = usePalette();
  // While typing keep the raw text ("12." would otherwise turn into "12.00" mid-word).
  const [draft, setDraft] = useState(text);
  useEffect(() => {
    if (!editing) setDraft(text);
  }, [editing, text]);
  const ts = { fontSize: font.footnote, fontWeight: '600' as const, color: p.text, lineHeight: 18 };
  return (
    <View
      accessibilityLabel={label}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: spacing.m, paddingVertical: 6, borderRadius: radius.pill, backgroundColor: p.card, borderWidth: StyleSheet.hairlineWidth, borderColor: p.border }}>
      <Icon name={icon} size={16} color={p.tint} />
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        {prefix ? <Text style={ts}>{prefix}</Text> : null}
        <EditableText
          editing={editing}
          value={editing ? draft : text}
          onChangeText={v => {
            setDraft(v);
            onChange(v);
          }}
          style={ts}
          multiline={false}
          keyboardType={keyboardType}
          accessibilityLabel={label}
          fieldColor={p.border}
          inset={{ x: 2, y: 1 }}
        />
        {suffix ? <Text style={ts}>{suffix}</Text> : null}
      </View>
    </View>
  );
}

/**
 * An ingredient/utensil chip. Tapping it opens its editor; in edit mode its icon turns into a
 * selection circle on the same spot.
 */
function ItemChip({
  label, icon, editing, selected, onToggle, onOpen,
}: { label: string; icon: IconName; editing: boolean; selected: boolean; onToggle: () => void; onOpen?: () => void }) {
  const p = usePalette();
  const { t } = useTranslation();
  return (
    <Pressable
      disabled={!onOpen}
      onPress={onOpen}
      accessibilityRole={onOpen ? 'button' : undefined}
      style={({ pressed }) => ({
        flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: spacing.m, paddingVertical: 6, borderRadius: radius.pill, maxWidth: '100%',
        backgroundColor: p.card, borderWidth: StyleSheet.hairlineWidth, borderColor: selected && editing ? p.success : p.border, opacity: pressed ? 0.7 : 1,
      })}>
      <Pressable
        disabled={!editing}
        onPress={onToggle}
        hitSlop={10}
        accessibilityRole={editing ? 'checkbox' : undefined}
        accessibilityState={editing ? { checked: selected } : undefined}
        accessibilityLabel={editing ? `${t('recipe.selectItem')}: ${label}` : undefined}>
        <Swap
          showB={editing}
          a={<Icon name={icon} size={16} color={p.tint} />}
          b={<Icon name={selected ? 'checkCircle' : 'circle'} size={16} color={selected ? p.success : p.secondaryText} />}
        />
      </Pressable>
      <Text style={{ flexShrink: 1, fontSize: font.footnote, fontWeight: '600', color: p.text }}>{label}</Text>
    </Pressable>
  );
}

function b2key(b: Badge) {
  return ({ breaks: 'recipe.dietBreaks', issue: 'recipe.dietIssue', unreviewed: 'recipe.dietUnreviewed', confirmed: 'recipe.dietConfirmed', none: 'recipe.dietNone' } as const)[b.key as 'issue'] ?? 'recipe.dietNone';
}
function b2keyPantry(b: Badge) {
  return ({ unknown: 'recipe.pantryUnknown', missing: 'recipe.pantryMissing', all: 'recipe.pantryAll' } as const)[b.key as 'unknown'] ?? 'recipe.pantryAll';
}

function StepView({
  step, index, editing, selected, onToggle, itemTitle, onChange, onOpen, onStartTimer,
}: {
  step: Step; index: number; editing: boolean; selected: boolean; onToggle: () => void;
  itemTitle: (kind: 'ingredient' | 'utensil', id: string) => string; onChange: (patch: Partial<Step>) => void;
  onOpen: () => void; onStartTimer: (label: string, seconds: number) => void;
}) {
  const { t } = useTranslation();
  const p = usePalette();
  const timers = [...step.timers, ...detectTimers(`${step.title} ${step.explainer}`).filter(d => !step.timers.some(x => x.seconds === d.seconds))];
  // The notes line exists when there are notes; while editing it stays even if emptied.
  const [notesLine, setNotesLine] = useState(!!step.explainer);
  useEffect(() => {
    if (step.explainer) setNotesLine(true);
    else if (!editing) setNotesLine(false);
  }, [step.explainer, editing]);

  return (
    <View style={{ gap: spacing.s }}>
      {/* The card fades in around the step (outside its box), so the text does not move. */}
      <Fade visible={editing} style={{ position: 'absolute', top: -6, bottom: -6, left: -10, right: -10, backgroundColor: p.card, borderRadius: radius.l }} />
      {step.image ? <Photo media={step.image} seed={step.id} style={{ width: '100%', aspectRatio: 16 / 9 }} /> : null}
      <View style={{ flexDirection: 'row', gap: spacing.m, alignItems: 'flex-start' }}>
        <Swap
          showB={editing}
          style={{ width: 28 }}
          bStyle={{ alignItems: 'flex-start', justifyContent: 'flex-start' }}
          a={<Text style={{ fontSize: font.headline, fontWeight: '800', color: p.tint, lineHeight: 22 }}>{index + 1}.</Text>}
          b={
            <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: selected }} accessibilityLabel={t('recipe.step', { n: index + 1 })} onPress={onToggle} hitSlop={8}>
              <Icon name={selected ? 'checkCircle' : 'circle'} color={selected ? p.success : p.secondaryText} size={22} />
            </Pressable>
          }
        />
        <View style={{ flex: 1, gap: spacing.xs }}>
          <EditableText editing={editing} value={step.title} onChangeText={v => onChange({ title: v })} placeholder={t('recipe.describeInstruction')} fieldColor={p.background}
            style={{ fontSize: font.body, fontWeight: '600', color: p.text, lineHeight: 22 }} />
          {notesLine ? (
            <EditableText editing={editing} value={step.explainer} onChangeText={v => onChange({ explainer: v })} placeholder={t('common.notes')} fieldColor={p.background}
              style={{ fontSize: font.body, color: p.secondaryText, lineHeight: 22 }} />
          ) : null}
        </View>
        <Fade visible={editing}>
          <IconButton icon="more" label={t('recipe.editStep')} onPress={onOpen} />
        </Fade>
      </View>
      <Row>
        {step.ingredients.map(l => (
          <Chip key={l.ingredientId} icon="ingredient" label={itemTitle('ingredient', l.ingredientId)} />
        ))}
        {step.utensils.map(l => (
          <Chip key={l.utensilId} icon="utensil" label={itemTitle('utensil', l.utensilId)} />
        ))}
        {timers.map((tm, ti) => (
          <Chip key={`t${ti}`} icon="timer" color={p.tint} label={t('cook.startTimer', { time: formatClock(tm.seconds) })} onPress={editing ? undefined : () => onStartTimer(tm.label, tm.seconds)} />
        ))}
      </Row>
    </View>
  );
}

/** Everything about one step that is not its text: notes, linked items, timers, order, photo. */
function StepSheet({
  step, index, count, recipe, itemTitle, onClose, onChange, onMove, onDelete,
}: {
  step: Step | undefined; index: number; count: number; recipe: Recipe; itemTitle: (id: string) => string; onClose: () => void;
  onChange: (patch: Partial<Step>) => void; onMove: (dir: -1 | 1) => void; onDelete: () => void;
}) {
  const { t } = useTranslation();
  const p = usePalette();
  const [timerMinutes, setTimerMinutes] = useState('');
  if (!step) return <Sheet visible={false} onClose={onClose}>{null}</Sheet>;
  const linked = new Set(step.ingredients.map(l => l.ingredientId));
  const toggleLink = (l: IngredientLine) =>
    onChange({ ingredients: linked.has(l.ingredientId) ? step.ingredients.filter(x => x.ingredientId !== l.ingredientId) : [...step.ingredients, l] });
  return (
    <Sheet visible onClose={onClose} title={t('recipe.step', { n: index + 1 })}>
      <Field label={t('common.notes')} value={step.explainer} onChangeText={v => onChange({ explainer: v })} multiline style={{ minHeight: 64, textAlignVertical: 'top' }} />
      <Caption>{t('recipe.linkedItems')}</Caption>
      <Row>
        {recipe.ingredients.map(l => (
          <Chip key={l.ingredientId} label={itemTitle(l.ingredientId)} selected={linked.has(l.ingredientId)} onPress={() => toggleLink(l)} />
        ))}
      </Row>
      <Caption>{t('recipe.timers')}</Caption>
      <Row>
        {step.timers.map((tm, ti) => (
          <Chip key={ti} icon="close" label={`${tm.label} ${formatClock(tm.seconds)}`} onPress={() => onChange({ timers: step.timers.filter((_, j) => j !== ti) })} />
        ))}
        <Field value={timerMinutes} onChangeText={setTimerMinutes} placeholder={t('recipe.timerMinutes')} keyboardType="number-pad" style={{ width: 100 }} />
        <IconButton
          icon="plus"
          label={t('recipe.addTimer')}
          onPress={() => {
            const minutes = parseFloat(timerMinutes.replace(',', '.'));
            if (minutes > 0) onChange({ timers: [...step.timers, { label: step.title.slice(0, 30), seconds: Math.round(minutes * 60) }] });
            setTimerMinutes('');
          }}
        />
      </Row>
      <Row gap={spacing.m}>
        <IconButton icon="up" label={t('recipe.moveUp')} disabled={index === 0} onPress={() => onMove(-1)} />
        <IconButton icon="down" label={t('recipe.moveDown')} disabled={index === count - 1} onPress={() => onMove(1)} />
        <IconButton
          icon="camera"
          label={t('recipe.stepMedia')}
          onPress={async () => {
            const ph = await pickPhoto();
            if (ph) onChange({ image: await savePhoto(ph) });
          }}
        />
        <IconButton icon="copy" label={t('recipe.copyInstruction')} onPress={() => copyText(step.title)} />
        <IconButton icon="trash" color={p.danger} label={t('recipe.deleteInstruction')} onPress={onDelete} />
      </Row>
    </Sheet>
  );
}
